import { useCallback, useMemo } from "react";
import type { Option } from "../../common/SelectBase";

const SELECT_ALL_TEXT = "Select all";
const SELECT_RELEVANT_TEXT = "Select relevant";

interface UseSelectMultipleReturn {
  /**
   * Whether all filtered options are selected
   */
  allSelected: boolean;

  /**
   * Whether all relevant filtered options are selected
   */
  relevantSelected: boolean;

  /**
   * Callback to toggle selection of all filtered options
   */
  toggleAllFiltered: () => void;

  /**
   * Text to display for the select all/relevant option
   */
  displayText: typeof SELECT_ALL_TEXT | typeof SELECT_RELEVANT_TEXT;
}

/**
 * Hook to manage "select all" or "select relevant" functionality for a list of options.
 *
 * @param selectedValues - Currently selected option values
 * @param filteredOptions - Options currently filtered/displayed
 * @param onChange - Callback to update selected values
 */
export function useSelectMultiple(
  selectedValues: string[],
  filteredOptions: Option[],
  onChange: (next: string[]) => void
): UseSelectMultipleReturn {
  const allOptionsRelevant = filteredOptions.every((option) => option.relevant);

  const allSelected = useMemo(
    () =>
      filteredOptions.length > 0 &&
      filteredOptions.every((option) => selectedValues.includes(option.value)),
    [filteredOptions, selectedValues]
  );

  // If every filteredOption that has relevant is found in selectedValues
  const relevantSelected = useMemo(() => {
    const relevantFiltered = filteredOptions.filter(
      (option) => option.relevant
    );

    if (
      relevantFiltered.every((option) => selectedValues.includes(option.value))
    ) {
      return true;
    }

    return false;
  }, [filteredOptions, selectedValues]);

  /**
   * Toggles selection of all filtered options.
   * - If all filtered options are selected, deselects them.
   * - Otherwise, selects all filtered options (preserving already selected values).
   */
  const toggleAllFiltered = useCallback(() => {
    if (!filteredOptions.length) {
      return;
    }

    const filteredValues = filteredOptions.map((option) => option.value);
    const allSelected = filteredValues.every((value) =>
      selectedValues.includes(value)
    );

    if (allSelected) {
      onChange(
        selectedValues.filter((value) => !filteredValues.includes(value))
      );
    } else if (selectedValues.length === 0 && !allOptionsRelevant) {
      onChange(
        filteredOptions
          .filter((value) => value.relevant)
          .map((option) => option.value)
      );
    } else {
      // Merge existing selected values with filtered values, avoiding duplicates
      onChange(Array.from(new Set([...selectedValues, ...filteredValues])));
    }
  }, [filteredOptions, onChange, selectedValues, allOptionsRelevant]);

  const displayText = useMemo(() => {
    if (selectedValues.length === 0 && !allOptionsRelevant) {
      return SELECT_RELEVANT_TEXT;
    }

    return SELECT_ALL_TEXT;
  }, [selectedValues, allOptionsRelevant]);

  return {
    allSelected,
    relevantSelected,
    toggleAllFiltered,
    displayText
  };
}
